import React, { useEffect } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import Slide from "@mui/material/Slide"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { PATH_INFOS } from "utils/navigatorUtils"
import { CLOSE_CREATE_NEW_LIST_OVERLAY, CUSTOM_BACK_BUTTON_CLICKED } from "constants/events"
import { updateShowCreateNewListOverlay } from "appRedux/slices/itemFormSlice"
import { useCreateWishListMutation } from "appRedux/slices/wishListApiSlice"
import useEventListener from "hooks/useEventListener"
import useBackListener from "hooks/useBackListener"
import PageTitle from "components/atoms/PageTitle"
import WishListForm from "components/templates/WishListForm"

type CreateNewListModalProps = {
  show: boolean;
}

export default function CreateNewListOverlay(props: CreateNewListModalProps) {
  const { show } = props

  const [createWishList, createWishListResult] = useCreateWishListMutation()

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEventListener(document, CUSTOM_BACK_BUTTON_CLICKED, handleCustomBackButtonClicked)

  useBackListener(show, handleBackListenerCallback)

  useEffect(() => {
    if (createWishListResult.status === "fulfilled") {
      handleWishListCreatedSuccessfully()
    }
  }, [createWishListResult])

  function handleBackListenerCallback() {
    document.dispatchEvent(new CustomEvent(CLOSE_CREATE_NEW_LIST_OVERLAY))
  }

  function handleCustomBackButtonClicked() {
    navigate(-1)
  }

  function handleWishListCreatedSuccessfully() {
    dispatch(updateShowCreateNewListOverlay(false))
    handleCustomBackButtonClicked()
  }

  function isCTADisabled(dirtyFields: object) {
    return !dirtyFields.hasOwnProperty("name") || createWishListResult.isLoading
  }

  function handleCreateNewList(value: WishListRequestPayload) {
    createWishList(value)
  }

  return createPortal(
    <Slide
      id="create-new-list-overlay-container"
      direction="left"
      in={show}
      timeout={150}
      easing={{
        enter: "ease-in-out",
        exit: "linear"
      }}
      style={{
        width: "100%",
        maxWidth: "var(--max-container-width)",
        position: "fixed",
        top: 0,
        zIndex: 10000
      }}
    >
      <Container>
        <PageTitle
          title={t(PATH_INFOS.list.title)}
          hasBackButton={true}
          customBackButtonEvent={CLOSE_CREATE_NEW_LIST_OVERLAY}
          headerButtons={{ right: [], left: [] }}
        />
        <Wrapper>
          <WishListForm
            isCTADisabled={isCTADisabled}
            isCTALoading={() => createWishListResult.isLoading}
            onSubmit={handleCreateNewList}
          />
        </Wrapper>
      </Container>
    </Slide>,
    document.body
  )
}

const Container = styled("div")(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vh",
  backgroundColor: theme.palette.gray["3"],
  padding: "0 0 30px",

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const Wrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  height: "calc(100vh - 48px)",
  padding: "8px 16px 30px",
  marginTop: "48px",

  "@supports (height: 100dvh)": {
    height: "calc(100dvh - 48px)"
  }
}))
